import { useEffect, useMemo, useState } from "react";
import { Plus, Printer, Trash2, Save, ListPlus, Tags } from "lucide-react";
import { Page, Panel, SearchInput, MoneyInput } from "../lib/ui";
import { api, can, moneyCur } from "../lib/api";
import { StockCombobox } from "./Modals";

const itemFromStock = (s) => ({ stock_id: s.id, code: s.code, name: s.name, unit: s.unit || "", category: s.category || "", currency: s.currency || "TRY", price: 0 });

function ListSidebar({ lists, activeId, onPick, onCreate, editable }) {
  return (
    <aside className="pl-sidebar" data-testid="price-lists-sidebar">
      {editable && (
        <button className="primary-button" data-testid="price-list-new" onClick={onCreate}><Plus size={15} /> Yeni fiyat listesi ekle</button>
      )}
      {lists.map((l) => (
        <button key={l.id} type="button" data-testid={`price-list-item-${l.id}`}
                className={l.id === activeId ? "pl-item active" : "pl-item"} onClick={() => onPick(l.id)}>
          <Tags size={14} /><span>{l.name}</span><small>{l.items?.length || 0} kalem</small>
        </button>
      ))}
      {!lists.length && <p className="empty" data-testid="price-lists-empty">Henüz fiyat listesi yok.</p>}
    </aside>
  );
}

function ListEditor({ list, stocks, editable, onSaved, onDeleted }) {
  const [draft, setDraft] = useState(list);
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDraft(list); setMsg(""); }, [list.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const setItem = (i, patch) => setDraft((d) => ({ ...d, items: d.items.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));
  const addStock = (s) => {
    if (draft.items.some((x) => x.stock_id === s.id)) return setMsg(`${s.name} zaten listede`);
    setDraft((d) => ({ ...d, items: [...d.items, itemFromStock(s)] }));
  };
  const addAll = () => {
    const have = new Set(draft.items.map((x) => x.stock_id));
    setDraft((d) => ({ ...d, items: [...d.items, ...stocks.filter((s) => !have.has(s.id)).map(itemFromStock)] }));
  };
  const save = async () => {
    try {
      const { data } = await api.put(`/price-lists/${list.id}`, { name: draft.name, note: draft.note, items: draft.items.map((x) => ({ ...x, price: Number(x.price || 0) })) });
      setMsg("Fiyat listesi kaydedildi"); onSaved(data);
    } catch (e) { setMsg(e.response?.data?.detail || "Kaydedilemedi"); }
  };
  const remove = async () => {
    if (!window.confirm(`"${list.name}" fiyat listesi silinsin mi?`)) return;
    await api.delete(`/price-lists/${list.id}`); onDeleted(list.id);
  };
  const shown = useMemo(() => draft.items.map((x, i) => ({ ...x, i }))
    .filter((x) => !q.trim() || `${x.code} ${x.name} ${x.category}`.toLowerCase().includes(q.trim().toLowerCase())), [draft.items, q]);

  return (
    <Panel
      title={editable ? (
        <input className="pl-name-input" data-testid="price-list-name-input" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      ) : draft.name}
      subtitle={`${draft.items.length} kalem · fiyatlar stok kartından bağımsızdır`}
      action={
        <div className="button-row">
          <button className="secondary-button" data-testid="price-print" onClick={() => window.print()}><Printer size={15} /> Yazdır</button>
          {editable && <button className="secondary-button tone-danger" data-testid="price-list-delete" onClick={remove}><Trash2 size={15} /> Sil</button>}
          {editable && <button className="primary-button" data-testid="price-list-save" onClick={save}><Save size={15} /> Kaydet</button>}
        </div>
      }
    >
      {editable && (
        <div className="install-toolbar">
          <div className="pl-add-stock" data-testid="price-list-add-stock">
            <StockCombobox stocks={stocks} selectedId="" selectedLabel="" index="pl" onSelect={addStock} />
          </div>
          <button className="secondary-button" data-testid="price-list-add-all" onClick={addAll}><ListPlus size={15} /> Tüm stokları ekle</button>
          <SearchInput testid="price-search" value={q} onChange={setQ} placeholder="Listede ara…" />
        </div>
      )}
      {msg && <div className="sync-msg" data-testid="price-list-msg">{msg}</div>}
      <div className="table-wrap" data-testid="price-table">
        <table>
          <thead><tr><th>Kod</th><th>Ürün</th><th>Kategori</th><th>Birim</th><th className="amount">Liste fiyatı</th><th></th></tr></thead>
          <tbody>
            {shown.length ? shown.map((x) => (
              <tr key={`${x.stock_id}-${x.i}`} data-testid={`price-row-${x.i}`}>
                <td><strong>{x.code}</strong></td>
                <td>{x.name}</td>
                <td>{x.category || "—"}</td>
                <td>{x.unit || "—"}</td>
                <td className="amount">
                  {editable ? (
                    <span className="price-edit">
                      <MoneyInput value={x.price} testid={`price-input-${x.i}`} onChange={(e) => setItem(x.i, { price: e.target.value })} />
                      <select value={x.currency} data-testid={`price-currency-${x.i}`} onChange={(e) => setItem(x.i, { currency: e.target.value })}>
                        <option value="TRY">₺</option><option value="USD">$</option><option value="EUR">€</option>
                      </select>
                    </span>
                  ) : <strong>{moneyCur(x.price, x.currency)}</strong>}
                </td>
                <td>{editable && (
                  <button className="row-icon tone-danger" data-testid={`price-remove-${x.i}`} title="Listeden çıkar"
                          onClick={() => setDraft((d) => ({ ...d, items: d.items.filter((_, idx) => idx !== x.i) }))}><Trash2 size={14} /></button>
                )}</td>
              </tr>
            )) : <tr><td colSpan={6} className="empty">Listeye stoktan ürün ekleyin.</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

export default function PriceList({ user, stocks = [] }) {
  const [lists, setLists] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const editable = can(user, "edit_price_list");

  useEffect(() => {
    api.get("/price-lists").then((r) => { setLists(r.data); setActiveId(r.data[0]?.id || null); }).catch(() => setLists([]));
  }, []);

  const create = async () => {
    const name = window.prompt("Yeni fiyat listesinin adı (örn. Toptan 2026, Perakende):", "");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/price-lists", { name: name.trim(), items: [] });
      setLists((l) => [...l, data]); setActiveId(data.id);
    } catch (e) { alert(e.response?.data?.detail || "Liste oluşturulamadı"); }
  };
  const active = lists.find((l) => l.id === activeId);

  return (
    <>
      <Page eyebrow="FİYATLANDIRMA" title="Fiyat Listeleri"
            subtitle="İstediğiniz kadar isimli fiyat listesi oluşturun; stoklardaki ürünlere elle fiyat girin. Stok kartı fiyatları değişmez." />
      <div className="pl-layout">
        <ListSidebar lists={lists} activeId={activeId} onPick={setActiveId} onCreate={create} editable={editable} />
        {active ? (
          <ListEditor list={active} stocks={stocks} editable={editable}
                      onSaved={(d) => setLists((l) => l.map((x) => (x.id === d.id ? d : x)))}
                      onDeleted={(id) => { const rest = lists.filter((x) => x.id !== id); setLists(rest); setActiveId(rest[0]?.id || null); }} />
        ) : (
          <Panel title="Fiyat listesi seçin"><p className="empty">Soldan bir liste seçin veya yeni liste ekleyin.</p></Panel>
        )}
      </div>
    </>
  );
}
